import { Nav } from "@foldkit/ui";
import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";
import * as lucide from "lucide";

import type { ClerkMounts } from "../auth/mounts";
import { i18next } from "../i18n";
import { BrandLogo } from "../widgets/brandLogo";
import {
	countsTotal,
	folderTreeView,
	treeFromCounts,
} from "../widgets/folderTree";
import {
	Biohazard,
	Bot,
	Calendar,
	CheckSquare,
	ChevronRight,
	CreditCard,
	type IconOptions,
	Link,
	List,
	Menu,
	MessageSquare,
	Search,
	Settings,
	Sparkles,
	StickyNote,
	User,
	X,
	icon,
} from "../widgets/icons";
import * as Sidebar from "../widgets/sidebar";
import { Message } from "./message";
import { type Model, a2aContextEnabled, isAdmin, isOwner } from "./model";
import { Route, linkHref } from "./routes";
import { timeTravelPicker } from "./timeTravel";

/**
 * The shell (U16): a grouped left sidebar replaces the busy top bar. People
 * areas first, the agent areas (WORK, behind `a2a-context`) next, and
 * account/admin areas pinned to the bottom. The top bar keeps only search
 * (Cmd/Ctrl-K), the time-travel chip and the account menu. From `lg` the
 * sidebar is fixed and collapses to icons (remembered per browser); below it
 * the same sidebar is the DaisyUI drawer.
 */

type NavItem =
	| "Home"
	| "Records"
	| "KeepInTouch"
	| "Tasks"
	| "Knowledge"
	| "Agents"
	| "Integrations"
	| "Billing"
	| "Developer"
	| "AI"
	| "Admin"
	| "Settings";

const none = Option.none<string>();
const targets: Record<NavItem, Route> = {
	Home: Route.Home({ asOf: none, q: none }),
	Records: Route.Records({ asOf: none, q: none }),
	KeepInTouch: Route.KeepInTouch({ asOf: none }),
	Tasks: Route.Tasks({ asOf: none }),
	Knowledge: Route.Knowledge({ asOf: none }),
	Agents: Route.Agents({ asOf: none }),
	Integrations: Route.Integrations({ asOf: none }),
	Billing: Route.Credits({ asOf: none, sessionId: none }),
	Developer: Route.Developer({ asOf: none }),
	AI: Route.AI({ asOf: none }),
	Admin: Route.Admin({ asOf: none }),
	Settings: Route.Settings({ asOf: none }),
};

const labels: Record<NavItem, () => string> = {
	Home: () => i18next.t("nav.today"),
	Records: () => i18next.t("nav.records"),
	KeepInTouch: () => i18next.t("nav.keepInTouch"),
	Tasks: () => i18next.t("nav.tasks"),
	Knowledge: () => i18next.t("nav.knowledge"),
	Agents: () => i18next.t("nav.agents"),
	Integrations: () => i18next.t("nav.integrations"),
	Billing: () => i18next.t("nav.creditUsage"),
	Developer: () => i18next.t("nav.developer", { defaultValue: "Developer" }),
	AI: () => i18next.t("nav.ai", { defaultValue: "AI" }),
	Admin: () => i18next.t("nav.admin", { defaultValue: "Admin" }),
	Settings: () => i18next.t("nav.settings"),
};

const icons: Record<
	NavItem,
	(h: HtmlBuilder<Message>, options?: IconOptions) => Html
> = {
	Home: Calendar,
	Records: List,
	KeepInTouch: MessageSquare,
	Tasks: CheckSquare,
	Knowledge: StickyNote,
	Agents: Bot,
	Integrations: Link,
	Billing: CreditCard,
	Developer: Biohazard,
	AI: Sparkles,
	Admin: User,
	Settings,
};

/** The sidebar's three groups; WORK exists only behind the a2a-context gate, admin areas only for those who see them. */
export const navGroups = (model: Model) => ({
	main: ["Home", "Records", "KeepInTouch"] as ReadonlyArray<NavItem>,
	work: (a2aContextEnabled(model)
		? ["Tasks", "Knowledge", "Agents"]
		: []) as ReadonlyArray<NavItem>,
	more: [
		...(model.integrationsEnabled ? ["Integrations"] : []),
		...(model.billingPreviewEnabled ? ["Billing"] : []),
		...(isAdmin(model) ? ["Developer", "AI"] : []),
		...(isOwner(model) ? ["Admin"] : []),
		"Settings",
	] as ReadonlyArray<NavItem>,
});

export const navItems = (model: Model): ReadonlyArray<NavItem> => {
	const { main, work, more } = navGroups(model);
	return [...main, ...work, ...more];
};

export const drawerToggleId = "main-drawer";

export const creditsHref = (model: Model): string =>
	linkHref(model.route, targets.Billing);

/** A detail route (Record, Task, Note) keeps its list's item current. */
const isCurrent = (model: Model, item: NavItem): boolean =>
	model.route._tag === item ||
	(item === "Billing" && model.route._tag === "Credits") ||
	(item === "Tasks" && model.route._tag === "Task") ||
	(item === "Knowledge" && model.route._tag === "Note") ||
	(item === "Records" && model.route._tag === "Record");

const toSidebar = (message: Sidebar.Message): Message =>
	Message.GotSidebarMessage({ message });

/** Never equal to a folder path, so no row is current (a note's folder is not in the route). */
const NO_FOLDER = "\u0000";

const folderTree = (model: Model, h: HtmlBuilder<Message>): Html => {
	const { sidebar, route } = model;
	const shown = sidebar.foldersOpen;
	return Option.match(AsyncData.getData(sidebar.folders), {
		onNone: () => h.empty,
		onSome: (counts) =>
			!shown || counts.folders.length === 0
				? h.empty
				: h.div(
						[
							h.DataAttribute("testid", "sidebar-folders"),
							// Folders need labels: hidden while collapsed to icons.
							h.Class(
								`mt-0.5 ms-3 border-s border-base-300 ps-1 ${sidebar.collapsed ? "lg:hidden" : ""}`,
							),
						],
						[
							folderTreeView<Message>(h, {
								nodes: treeFromCounts(counts),
								allCount: countsTotal(counts),
								selected:
									route._tag === "Knowledge"
										? Option.getOrNull(route.folder ?? Option.none())
										: NO_FOLDER,
								toggled: sidebar.folderToggled,
								href: (folder) =>
									linkHref(
										route,
										Route.Knowledge({
											asOf: none,
											folder: Option.fromNullishOr(folder),
										}),
									),
								onToggle: (path) =>
									toSidebar(Sidebar.Message.ClickedToggleFolder({ path })),
								drop: undefined,
							}),
						],
					),
	});
};

const waitingBadge = (
	h: HtmlBuilder<Message>,
	count: number,
	collapsed: boolean,
): Html =>
	count === 0
		? h.empty
		: h.span(
				[
					h.DataAttribute("testid", "agents-waiting"),
					h.AriaHidden(true),
					h.Class(
						`badge badge-primary badge-sm ms-auto tabular-nums ${collapsed ? "lg:absolute lg:-top-0.5 lg:end-0.5 lg:badge-xs lg:ms-0" : ""}`,
					),
				],
				[String(count)],
			);

const sidebarNav = (model: Model, h: HtmlBuilder<Message>): Html => {
	const { collapsed } = model.sidebar;
	const groups = navGroups(model);
	const waiting = Sidebar.waitingCount(model.sidebar);
	return Nav.view<NavItem>({
		items: navItems(model),
		ariaLabel: i18next.t("nav.main"),
		toHref: (item) => linkHref(model.route, targets[item]),
		isItemCurrent: (item) => isCurrent(model, item),
		toView: ({ nav: attributes, items }) => {
			const link = (item: (typeof items)[number]): Html => {
				const label = labels[item.value]();
				const name =
					item.value === "Agents" && waiting > 0
						? i18next.t("nav.agentsWaiting", { count: waiting, name: label })
						: label;
				const folders =
					item.value === "Knowledge" &&
					Option.exists(
						AsyncData.getData(model.sidebar.folders),
						(counts) => counts.folders.length > 0,
					);
				const foldersShown = model.sidebar.foldersOpen;
				return h.li(
					[h.Key(item.value)],
					[
						h.div(
							[h.Class("relative flex items-center p-0")],
							[
								h.a(
									[
										...item.link,
										h.AriaLabel(name),
										h.Title(name),
										h.DataAttribute("testid", `nav-${item.value}`),
										h.Class(
											`flex min-w-0 flex-1 items-center gap-3 rounded-field px-3 py-2 ${collapsed ? "lg:justify-center lg:px-0" : ""} ${item.isCurrent ? "bg-primary/10 font-medium text-primary" : "text-base-content/80 hover:bg-base-200"}`,
										),
									],
									[
										icons[item.value](h, { size: 18, class: "shrink-0" }),
										h.span(
											[h.Class(`truncate ${collapsed ? "lg:sr-only" : ""}`)],
											[label],
										),
										...(item.value === "Agents"
											? [waitingBadge(h, waiting, collapsed)]
											: []),
									],
								),
								folders
									? h.button(
											[
												h.Type("button"),
												h.Class(
													`btn btn-ghost btn-xs btn-square absolute end-1 ${collapsed ? "lg:hidden" : ""}`,
												),
												h.AriaExpanded(foldersShown),
												h.AriaLabel(
													i18next.t(
														foldersShown
															? "nav.hideFolders"
															: "nav.showFolders",
													),
												),
												h.OnClick(
													toSidebar(Sidebar.Message.ClickedToggleFolders()),
												),
											],
											[
												ChevronRight(h, {
													size: 14,
													class: `transition-transform ${foldersShown ? "rotate-90" : ""}`,
												}),
											],
										)
									: h.empty,
							],
						),
						item.value === "Knowledge" ? folderTree(model, h) : h.empty,
					],
				);
			};
			const group = (group: ReadonlyArray<NavItem>, className: string): Html =>
				h.ul(
					[h.Class(`flex w-full flex-col gap-0.5 ${className}`)],
					items.filter((item) => group.includes(item.value)).map(link),
				);
			return h.nav(
				[
					...attributes,
					h.Class("flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-2"),
				],
				[
					group(groups.main, ""),
					groups.work.length === 0
						? h.empty
						: h.div(
								[h.DataAttribute("testid", "nav-work")],
								[
									h.h2(
										[
											h.Class(
												`px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-base-content/50 ${collapsed ? "lg:sr-only" : ""}`,
											),
										],
										[i18next.t("nav.work")],
									),
									collapsed
										? h.div(
												[
													h.AriaHidden(true),
													h.Class(
														"mx-3 mb-2 hidden border-t border-base-300 lg:block",
													),
												],
												[],
											)
										: h.empty,
									group(groups.work, ""),
								],
							),
					group(groups.more, "mt-auto border-t border-base-300 pt-2"),
				],
			);
		},
	});
};

const panelIcon = (h: HtmlBuilder<Message>, collapsed: boolean): Html =>
	collapsed
		? icon(h, "PanelLeftOpen", lucide.PanelLeftOpen, { size: 18 })
		: icon(h, "PanelLeftClose", lucide.PanelLeftClose, { size: 18 });

/** The sidebar; below `lg` it is the drawer (the toggle lives on the shell's checkbox). */
export const sidebar = (
	model: Model,
	mounts: ClerkMounts,
	h: HtmlBuilder<Message>,
): Html => {
	const { collapsed } = model.sidebar;
	const toggleLabel = i18next.t(
		collapsed ? "nav.expandSidebar" : "nav.collapseSidebar",
	);
	return h.div(
		[h.Class("drawer-side z-40")],
		[
			h.label(
				[
					h.For(drawerToggleId),
					h.AriaLabel(i18next.t("layout.closeSidebar")),
					h.OnClick(Message.ClickedCloseNavigation()),
					h.Class("drawer-overlay"),
				],
				[],
			),
			h.aside(
				[
					h.DataAttribute("testid", "sidebar"),
					h.DataAttribute("collapsed", String(collapsed)),
					h.Class(
						`flex h-full min-h-full w-64 max-w-[85vw] flex-col gap-3 border-e border-base-300 bg-base-100 pb-3 ${collapsed ? "lg:w-16" : ""}`,
					),
				],
				[
					h.div(
						[
							h.Class(
								`flex h-14 shrink-0 items-center gap-2 px-4 ${collapsed ? "lg:h-auto lg:flex-col lg:justify-center lg:px-0 lg:pt-3" : ""}`,
							),
						],
						[
							h.a(
								[
									h.Class(
										"flex shrink-0 items-center gap-2 font-display text-xl",
									),
									h.AriaLabel(i18next.t("navbar.rhysomn")),
									h.Href(linkHref(model.route, targets.Home)),
								],
								[
									BrandLogo(h),
									h.span(
										[h.Class(collapsed ? "lg:hidden" : "")],
										[i18next.t("navbar.rhysomn")],
									),
								],
							),
							h.label(
								[
									h.For(drawerToggleId),
									h.AriaLabel(i18next.t("layout.closeSidebar")),
									h.OnClick(Message.ClickedCloseNavigation()),
									h.Class("btn btn-ghost btn-sm btn-square ms-auto lg:hidden"),
								],
								[X(h, { size: 18 })],
							),
						],
					),
					searchButton(collapsed, h),
					sidebarNav(model, h),
					h.p(
						[
							h.Class(
								`px-4 text-xs leading-snug text-base-content/60 ${collapsed ? "lg:hidden" : ""}`,
							),
						],
						[i18next.t("layout.previewNotice")],
					),
					// Clerk restyles its host, so the padding lives on a wrapper.
					h.div(
						[
							h.Class(
								`flex shrink-0 items-center px-4 ${collapsed ? "lg:justify-center lg:px-0" : ""}`,
							),
						],
						[
							h.div(
								[
									h.Key(
										`user-menu-${isAdmin(model)}-${model.integrationsEnabled}-${model.billingPreviewEnabled}-${collapsed}`,
									),
									h.DataAttribute("testid", "user"),
									h.OnClick(Message.ClickedAccountMenu()),
									h.OnMount(
										mounts.UserButton({
											isAdmin: isAdmin(model),
											integrationsEnabled: model.integrationsEnabled,
											billingPreviewEnabled: model.billingPreviewEnabled,
											showName: !collapsed,
										}),
									),
								],
								[],
							),
						],
					),
					h.div(
						[
							h.Class(
								`flex shrink-0 items-center gap-1 px-3 ${collapsed ? "lg:flex-col lg:px-0" : ""}`,
							),
						],
						[
							timeTravelPicker(model, h),
							// Clerk restyles its host, so the collapsed rule lives on a wrapper.
							h.div(
								[h.Class(`min-w-0 flex-1 ${collapsed ? "lg:hidden" : ""}`)],
								[
									h.div(
										[
											h.DataAttribute("testid", "navbar-organization"),
											h.OnClick(Message.ClickedOrganizationSwitcher()),
											h.OnMount(mounts.OrganizationSwitcher()),
										],
										[],
									),
								],
							),
							h.button(
								[
									h.Type("button"),
									h.DataAttribute("testid", "sidebar-toggle"),
									h.Class(
										"btn btn-ghost btn-sm btn-square hidden text-base-content/70 lg:inline-flex",
									),
									h.AriaLabel(toggleLabel),
									h.Title(toggleLabel),
									h.AriaExpanded(!collapsed),
									h.OnClick(toSidebar(Sidebar.Message.ClickedToggleSidebar())),
								],
								[panelIcon(h, collapsed)],
							),
						],
					),
				],
			),
		],
	);
};

/** Cmd/Ctrl-K as a button: focuses the page's own search, or opens Records' search where the page has none. */
const searchButton = (collapsed: boolean, h: HtmlBuilder<Message>): Html =>
	h.div(
		[h.Class(`shrink-0 px-2 ${collapsed ? "lg:px-3" : ""}`)],
		[
			h.button(
				[
					h.Type("button"),
					h.DataAttribute("testid", "shell-search"),
					h.AriaLabel(i18next.t("nav.searchShort")),
					h.Title("⌘K"),
					h.OnClick(Message.PressedSearchShortcut()),
					h.Class(
						`flex w-full items-center gap-2 rounded-field border border-base-300 px-3 py-1.5 text-sm text-base-content/60 hover:bg-base-200 ${collapsed ? "lg:justify-center lg:px-0" : ""}`,
					),
				],
				[
					Search(h, { size: 16, class: "shrink-0" }),
					h.span(
						[
							h.Class(
								`flex-1 truncate text-start ${collapsed ? "lg:hidden" : ""}`,
							),
						],
						[i18next.t("nav.searchShort")],
					),
					h.kbd(
						[
							h.Class(
								`kbd kbd-xs hidden lg:inline-flex ${collapsed ? "lg:hidden" : ""}`,
							),
						],
						["⌘K"],
					),
				],
			),
		],
	);

/** Phones only: the drawer button and logo. From `lg` the sidebar carries everything, so pages start at the top edge. */
export const nav = (model: Model, h: HtmlBuilder<Message>): Html =>
	h.header(
		[
			h.DataAttribute("testid", "nav"),
			h.Class(
				"sticky top-0 z-30 flex h-12 shrink-0 items-center gap-2 border-b border-base-300 bg-base-100 px-2 lg:hidden",
			),
		],
		[
			h.label(
				[
					h.For(drawerToggleId),
					h.DataAttribute("testid", "open-drawer"),
					h.Class("btn btn-square btn-ghost btn-sm"),
					h.AriaLabel(i18next.t("layout.openNavMenu")),
					h.OnClick(Message.ClickedOpenNavigation()),
				],
				[Menu(h, { size: 20 })],
			),
			h.a(
				[
					h.Class("flex shrink-0 items-center"),
					h.AriaLabel(i18next.t("navbar.rhysomn")),
					h.Href(linkHref(model.route, targets.Home)),
				],
				[BrandLogo(h)],
			),
			// The phone bar names the current area: judges lost their place without it.
			Option.match(
				Option.fromNullishOr(
					navItems(model).find((item) => isCurrent(model, item)),
				),
				{
					onNone: () => h.empty,
					onSome: (item) =>
						h.span(
							[
								h.DataAttribute("testid", "nav-title"),
								h.Class("truncate font-semibold"),
							],
							[labels[item]()],
						),
				},
			),
		],
	);
